import { useSyncExternalStore } from 'react';

// Tiny global UI store (palette, toast, dialogs). Server data lives in react-query.
type Toast = { id: number; text: string; kind: 'ok' | 'error' };
interface UIState {
  palette: boolean;
  toast: Toast | null;
  repoPick: { projectId: string; issueKey: string } | null;
  modal: { kind: 'push' | 'ticket' | 'transition'; featureId: string } | null;
  /** Feature runs with a gate action in flight: every approve button for them is disabled. */
  featureBusy: Record<string, boolean>;
}

let state: UIState = { palette: false, toast: null, repoPick: null, modal: null, featureBusy: {} };
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export const ui = {
  get: () => state,
  set(patch: Partial<UIState>) {
    state = { ...state, ...patch };
    emit();
  },
  subscribe(fn: () => void) {
    subs.add(fn);
    return () => subs.delete(fn);
  },
};

export function useUI<T>(sel: (s: UIState) => T): T {
  return useSyncExternalStore(ui.subscribe, () => sel(state));
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(text: string, kind: 'ok' | 'error' = 'ok') {
  ui.set({ toast: { id: Date.now(), text, kind } });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => ui.set({ toast: null }), kind === 'error' ? 5000 : 2600);
}

/** Runs a gate action for a feature run, blocking other actions on that run until it settles. */
export async function featureAction<T>(featureId: string, fn: () => Promise<T>): Promise<T | undefined> {
  if (state.featureBusy[featureId]) return undefined;
  ui.set({ featureBusy: { ...state.featureBusy, [featureId]: true } });
  try {
    return await fn();
  } finally {
    const { [featureId]: _done, ...rest } = state.featureBusy;
    ui.set({ featureBusy: rest });
  }
}
